/**
 * WB4.7 P3+ — cross-route navigation revision session (sessionStorage SSOT).
 */

import type { WorkbenchRevisionSnapshotRecord } from './workbench-revision-snapshot';

const STORAGE_KEY = 'inflomatrix:workbench-nav-revision-v1';

type NavigationRevisionSessionMap = Record<string, WorkbenchRevisionSnapshotRecord>;

function isNavigationRevisionActive(
  snapshot: Pick<WorkbenchRevisionSnapshotRecord, 'expiresAt' | 'restoredAt'>
): boolean {
  if (snapshot.restoredAt) {
    return false;
  }
  const expiresAt = Date.parse(snapshot.expiresAt);
  if (Number.isNaN(expiresAt)) {
    return false;
  }
  return Date.now() < expiresAt;
}

function scopeKey(tenantId: string, objectId: string): string {
  return `${tenantId.trim()}:${objectId.trim()}`;
}

function readSessionMap(): NavigationRevisionSessionMap {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as NavigationRevisionSessionMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionMap(map: NavigationRevisionSessionMap): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const keys = Object.keys(map);
  if (keys.length === 0) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

export function persistNavigationRevisionSnapshot(record: WorkbenchRevisionSnapshotRecord): void {
  if (!isNavigationRevisionActive(record)) {
    return;
  }
  const map = readSessionMap();
  map[scopeKey(record.tenantId, record.objectId)] = record;
  writeSessionMap(map);
}

export function readNavigationRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): WorkbenchRevisionSnapshotRecord | null {
  const record = readSessionMap()[scopeKey(input.tenantId, input.objectId)];
  if (!record || !isNavigationRevisionActive(record)) {
    return null;
  }
  return record;
}

export function clearNavigationRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): void {
  const map = readSessionMap();
  const key = scopeKey(input.tenantId, input.objectId);
  if (!map[key]) {
    return;
  }
  delete map[key];
  writeSessionMap(map);
}

export function clearNavigationRevisionSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Cross-route restore — sessionStorage fallback when scope map is cold after navigation.
 */
export function resolveCrossRouteNavigationRestoreRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): WorkbenchRevisionSnapshotRecord | null {
  return readNavigationRevisionSnapshot(input);
}
